import { StrictMode } from 'react'
import type { FC } from 'react'
import { TabLabelPosition, TabSize } from '../Tabs'
import * as styles from './styles'
import { TabContents } from './TabContents'
import type { TabProps } from './types'

export type { TabProps }

/**
 * A single tab in a tab set.
 *
 * This component is compatible with Satellite’s `<Tabs.Tab>` and `react-tabs`,
 * but applies default styling, uses `<button>` instead of `<li>`, and has some
 * accessibility enhancements.
 *
 * @note This component is intentionally *not* exported from the package. Use
 *   the `<Tabs>` component, which implements additional behavior.
 */
export const Tab: FC<TabProps> & { tabsRole: string } = ({
  // ignored; react-tabs
  /* eslint-disable @typescript-eslint/no-unused-vars */
  disabledClassName,
  focus,
  selectedClassName,
  tabRef,
  value,
  /* eslint-enable @typescript-eslint/no-unused-vars */

  allowTranslation = true,
  children,
  controlsHtmlId,
  disabled,
  displayLabel = true,
  icon,
  label,
  labelPosition = TabLabelPosition.End,
  onClick,
  onKeyDown,
  panelId,
  selected,
  size = TabSize.Regular,
  styleOverrides = {},
  tabCount = 1,
  tabIndex,
  theme = 'light',
  ...props
}) => (
  <StrictMode>
    <button
      aria-controls={controlsHtmlId || panelId}
      aria-label={displayLabel ? undefined : label}
      aria-selected={selected}
      css={[
        styles.container({
          size,
          tabCount,
          theme,
        }),
        styleOverrides,
      ]}
      role="tab"
      tabIndex={tabIndex === undefined ? -1 : tabIndex}
      {...{
        disabled,
        onClick,
        onKeyDown,
      }}
      {...props}
    >
      <TabContents
        {...{
          allowTranslation,
          displayLabel,
          icon,
          label,
          labelPosition,
        }}
      >
        {children}
      </TabContents>
    </button>
  </StrictMode>
)
Tab.displayName = 'Lapidary Unstable: Tab'
// magic for `react-tabs`
Tab.tabsRole = 'Tab'
